'use client';

import { create } from 'zustand';

const STORAGE_KEY = 'admin-scope';

interface PersistedScope {
	cerId: string | null;
	configId: string | null;
}

function readScope(): PersistedScope {
	if (typeof window === 'undefined') return { cerId: null, configId: null };
	try {
		const raw = localStorage.getItem(STORAGE_KEY);
		const parsed = raw ? (JSON.parse(raw) as PersistedScope) : null;
		return {
			cerId: typeof parsed?.cerId === 'string' ? parsed.cerId : null,
			configId: typeof parsed?.configId === 'string' ? parsed.configId : null,
		};
	} catch {
		return { cerId: null, configId: null };
	}
}

function writeScope(scope: PersistedScope) {
	if (typeof window === 'undefined') return;
	localStorage.setItem(STORAGE_KEY, JSON.stringify(scope));
}

interface AdminScopeStore {
	/** CER selected in the admin area (Configurazioni / Soci / Onboarding). */
	cerId: string | null;
	/** Configuration (leaf org) the admin pages operate on — sent as ?orgId=. */
	configId: string | null;
	setCer: (cerId: string, firstConfigId: string | null) => void;
	setConfig: (configId: string) => void;
	setScope: (cerId: string | null, configId: string | null) => void;
}

/**
 * Scope of the admin area, independent from the global view selector: changing
 * the dashboard view never moves the admin pages, and vice versa.
 */
export const useAdminScopeStore = create<AdminScopeStore>((set) => ({
	...readScope(),
	setCer: (cerId, firstConfigId) => {
		writeScope({ cerId, configId: firstConfigId });
		set({ cerId, configId: firstConfigId });
	},
	setConfig: (configId) => {
		set((state) => {
			const next = { cerId: state.cerId, configId };
			writeScope(next);
			return next;
		});
	},
	setScope: (cerId, configId) => {
		writeScope({ cerId, configId });
		set({ cerId, configId });
	},
}));
